import Auth, { CognitoUser } from '@aws-amplify/auth';
import { registerInitialState } from '@modules/authentication/constants';
import { configureAndInitializeAmplify } from '@modules/authentication/utils/configureAndInitializeAmplify';
import { NAV_PATHS } from '@modules/common/constants';
import { useIsDomainSsoLazyQuery } from 'generated/graphql';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import AuthService from 'services/AuthService';
import { AuthContext, AuthState } from 'v2/core/clients/authAmplify/index';
import {
  AmplifyUser,
  ConfirmSignUpInput,
  ForgotPasswordInput,
  ResendSignUpInput,
  ResetPasswordInput,
  SignInInput,
  SignUpInput,
} from 'v2/core/clients/authAmplify/types';
import { configureAmplify } from 'v2/core/services/auth/utils/configureAmplify';

export const useAuth = (): AuthState => {
  const [user, setUser] = useState<CognitoUser | undefined>(undefined);

  const [verifyDomainSso] = useIsDomainSsoLazyQuery({
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const check = async () => {
      if (AuthService.getSSOViewAsDefault()) {
        // The user is authenticated via SSO and we need to
        // decide among with SAML or OIDC
        const email = AuthService.getEmail();
        if (email) {
          try {
            await configureAmplify({
              verifyDomainSso,
              email: AuthService.getEmail(),
              tenantSlug: AuthService.getTenantSlug(),
            });
            const user = await Auth.currentAuthenticatedUser();
            setUser(user);
          } catch {
            setUser(undefined);
          }
        }
        return;
      }

      const isSAMLRoute = window.location.pathname.includes(
        NAV_PATHS.LOGIN_LOADING_SAML,
      );

      if (isSAMLRoute) {
        configureAndInitializeAmplify(true, true);
      } else {
        configureAndInitializeAmplify();
      }

      const user = await Auth.currentAuthenticatedUser();
      setUser(user);
    };

    check();
  }, [setUser, verifyDomainSso]);

  const signIn = useCallback(
    async ({ email, password }: SignInInput) => {
      setUser(await Auth.signIn(email, password));
    },
    [setUser],
  );

  const signOut = useCallback(async () => {
    await Auth.signOut();
    setUser(undefined);
  }, [setUser]);

  const deleteUser = useCallback(async () => {
    user?.deleteUser((error?: Error) => {
      if (error) {
        throw error;
      }

      setUser(undefined);
    });
  }, [user, setUser]);

  return { user, signIn, signOut, deleteUser };
};

export const useGetAmplifyUser = (): AmplifyUser | undefined => {
  const { user } = React.useContext(AuthContext);
  if (!user) {
    return;
  }

  const email = AuthService.getEmail();
  const {
    'custom:firstName': firstName,
    'custom:lastName': lastName,
    'custom:role': role,
    // See https://github.com/aws-amplify/amplify-js/issues/4927
    // @ts-ignore
  } = user.attributes ?? registerInitialState;

  return {
    email,
    firstName,
    lastName,
    role,
    slug: AuthService.getTenantSlug(),
  };
};

export const useMustGetAmplifyUser = (): AmplifyUser => {
  const user = useGetAmplifyUser();
  if (!user) {
    throw Error('no authenticated user found');
  }
  return user;
};

export const useSignIn = () => {
  const context = useContext(AuthContext);
  return useCallback((input: SignUpInput) => context.signIn(input), [context]);
};

export const useSignOut = () => {
  const context = useContext(AuthContext);
  return useCallback(() => context.signOut(), [context]);
};

export const useAuthSignUp = () => {
  return async ({
    firstName,
    lastName,
    email,
    password,
    role,
  }: SignUpInput) => {
    await Auth.signUp({
      username: email,
      password,
      attributes: {
        email,
        'custom:firstName': firstName,
        'custom:lastName': lastName,
        'custom:role': role,
        'custom:signup_email': email,
      },
    });
  };
};

export const useAuthConfirmSignUp = () => {
  return async ({ email, code }: ConfirmSignUpInput) => {
    await Auth.confirmSignUp(email, code);
  };
};

export const useAuthResendSignUp = () => {
  return async ({ email }: ResendSignUpInput) => {
    await Auth.resendSignUp(email);
  };
};

export const useAuthForgotPassword = () => {
  return async ({ email }: ForgotPasswordInput) => {
    await Auth.forgotPassword(email);
  };
};

export const useAuthResetPassword = () => {
  return async ({ email, code, password }: ResetPasswordInput) => {
    await Auth.forgotPasswordSubmit(email, code, password);
  };
};

export const useDeleteUser = () => {
  return useContext(AuthContext).deleteUser;
};
